import type {
  DataGridColumnSettings,
  DataGridSettings,
  DynamicFormFieldType,
  JsonDataWrapper,
  ModalDialogButtonSetting,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
} from '@myosh/odin-components';
import { Button, DataGrid, ModalDialog, OdinDataSender } from '@myosh/odin-components';
import { isValid, parse } from 'date-fns';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SelectionSearchTypeProps } from '../../constants/enums';
import type { GetDetailsDataForChartColumnRateApiArg } from '../../redux/config/chart-data';
import { useGetDetailsDataForChartColumnRateMutation } from '../../redux/config/chart-data';
import { useDetailsWindowDataQuery, useDetailsWindowHeaderQuery } from '../../redux/config/details-window';
import { useTableFiltersQuery } from '../../redux/config/table-data';
import type { DetailsWindowFiltersProps } from '../../redux/config/table-data.interfaces';
import { useAppDispatch } from '../../redux/hooks';
import { setTableFilters } from '../../redux/slices/update-widget-table-sort-field';
import { useExportFile } from '../Charts/hooks/use-export-file';
import { useSelectedView } from '../Charts/hooks/use-selected-view';
import { useRecordOdinUrl } from '../Charts/hooks/useRecordOdinUrl';
import useStartEndDates from '../Charts/hooks/useStartEndDates';
import { formatDisplayDate } from '../common/date-util';
import { ErrorDataLabel } from '../common/error-data-label.component';
import { LoaderComponent } from '../common/loading-spinner.component';

export interface CustomModalProps extends DetailsConfigurationFieldsValues {
  field?: string;
  name: string;
  tooltipPayload: Array<{
    name: string;
    dataKey: string;
  }>;
}

export interface DetailsConfigurationFields {
  field: string;
  secondField: string;
  moduleViewId: string;
}

export interface DetailsConfigurationFieldsValues {
  fieldValue?: string;
  secondFieldValue?: string | boolean;
  recordIds?: Array<string>;
}

interface DetailsWindowProps extends DetailsConfigurationFields, DetailsConfigurationFieldsValues {
  widgetId: string;
  visible: boolean;
  onDialogHidden: () => void;
  isColumnRate?: boolean;
}

export default function OdinCustomDetailsWindow({
  widgetId,
  field,
  fieldValue,
  secondField,
  secondFieldValue,
  visible,
  onDialogHidden,
  moduleViewId,
  recordIds,
  isColumnRate,
}: DetailsWindowProps) {
  const [gridOptions, setGridOptions] = useState<OdinDataRetrievalOptions>();
  const [columns, setColumns] = useState<Array<DataGridColumnSettings>>();
  const [columnRateData, setColumnRateData] = useState<Record<string, unknown>[] | undefined>();
  const dataSender = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const templateSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const { startDate, endDate } = useStartEndDates();
  const columnFilters = useRef<DetailsWindowFiltersProps>({});
  const dispatch = useAppDispatch();
  const view = useSelectedView();

  const { exportWidgetHandler } = useExportFile({ widgetId });

  const { data: header, isFetching: headerFetching } = useDetailsWindowHeaderQuery(widgetId, { skip: !visible });

  const {
    data: tableData,
    isFetching,
    isError,
  } = useDetailsWindowDataQuery(
    {
      widgetId,
      field,
      fieldValue,
      secondField,
      secondFieldValue: secondFieldValue as string,
      page: gridOptions?.page || 1,
      pageSize: gridOptions?.pageSize || 50,
      fieldFilters: gridOptions?.fieldFilters,
      sortedFields: gridOptions?.sortedFields,
      recordIds,
    },
    { skip: !visible || header === undefined || headerFetching || isColumnRate }
  );
  const [getDetailsDataForChartColumnRate, { isLoading, isError: columnRateError }] =
    useGetDetailsDataForChartColumnRateMutation();
  const { data: tableFilterData } = useTableFiltersQuery({ widgetId }, { skip: !visible });

  const createDetailsUrl = useRecordOdinUrl(moduleViewId);
  const closeModalButton = useRef<Array<ModalDialogButtonSetting>>([
    {
      type: 'primary',
      name: 'closeModalButton',
      text: 'Close',
    },
  ]);

  const fetchColumnRateDetailData = useCallback(async () => {
    if (!visible || header === undefined || headerFetching || !isColumnRate) return;
    try {
      const payload: GetDetailsDataForChartColumnRateApiArg = {
        body: recordIds?.map((id) => Number(id)) ?? [],
        widgetId,
        page: gridOptions?.page || 1,
        pageSize: gridOptions?.pageSize || 50,
        fieldFilters: gridOptions?.fieldFilters,
        sortedFields: gridOptions?.sortedFields,
      };

      const result = await getDetailsDataForChartColumnRate(payload).unwrap();
      if (result) {
        setColumnRateData(result);
      }
    } catch {
      setColumnRateData([]);
    }
  }, [
    visible,
    header,
    headerFetching,
    isColumnRate,
    recordIds,
    widgetId,
    gridOptions,
    getDetailsDataForChartColumnRate,
  ]);

  useEffect(() => {
    if (header && !headerFetching) {
      const gridColumns: Array<DataGridColumnSettings> = header.map((column) => {
        const searchType = SelectionSearchTypeProps[column.type as keyof typeof SelectionSearchTypeProps] ?? undefined;
        const dropDownText = column.type === 'COMBOBOX' || column.type === 'MULTISELECTCOMBOBOX' ? 'field' : undefined;
        return {
          id: column.field,
          title: column.field,
          field: column.field,
          visible: true,
          isIdField: column.field === 'Doc Number',
          searchType: searchType,
          dropDownText: dropDownText,
          dropDownValue: dropDownText,
        };
      });
      setColumns(gridColumns);
    }
  }, [header, headerFetching]);

  useEffect(() => {
    if (isColumnRate && recordIds && recordIds?.length > 0) {
      fetchColumnRateDetailData();
    } else {
      setColumnRateData([]);
    }
  }, [startDate, endDate, view, gridOptions, recordIds, fetchColumnRateDetailData, isColumnRate]);

  useEffect(() => {
    const dataset = isColumnRate ? columnRateData : tableData;
    const loading = isColumnRate ? isLoading : isFetching;

    if (!dataset || loading) return;

    if (gridOptions?.page && gridOptions?.pageSize && dataset.length > 0) {
      const updatedData = dataset.map((item) => {
        const formattedItem: Record<string, unknown> = {};

        Object.entries(item).forEach(([key, value]) => {
          if (isDateLike(value)) {
            const parsedDate = new Date(value as string);
            formattedItem[key] = formatDisplayDate(parsedDate);
          } else {
            formattedItem[key] = value;
          }
        });

        return formattedItem;
      });

      dataSender.current?.sendData({ data: updatedData });
    } else {
      dataSender.current?.sendData();
    }
  }, [isColumnRate, gridOptions, tableData, columnRateData, isFetching, isLoading]);

  const dataRetrieval = useMemo<OdinDataRetrieval>(() => {
    return {
      getSubscriber: (subscriber, fieldType?: DynamicFormFieldType) => {
        if (fieldType !== 'COMBOBOX') {
          dataSender.current = subscriber;
        } else {
          templateSubscriber.current = subscriber;
        }
      },
      getData: (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options?.fieldType === 'COMBOBOX') {
          if (options.page === 1 && options.fieldId) {
            const fieldId = options.fieldId;
            const fieldKey = String(fieldId) as keyof typeof columnFilters.current;
            if (columnFilters.current[fieldKey]) {
              templateSubscriber.current?.sendData({
                data: columnFilters.current[fieldKey],
                requestId: options.requestId,
              });
            } else if (tableFilterData) {
              const dataResult = tableFilterData.find((el) => el.fieldId === fieldId)?.values;
              if (dataResult && dataResult.length >= 0) {
                columnFilters.current = { ...columnFilters.current, [String(fieldId)]: dataResult };
                templateSubscriber.current?.sendData({ data: dataResult, requestId: options.requestId });
              } else {
                templateSubscriber.current?.sendData();
              }
            }
          } else {
            templateSubscriber.current?.sendData();
          }
        } else {
          setGridOptions(options);
        }
      },
    };
  }, [tableFilterData]);

  useEffect(() => {
    dispatch(
      setTableFilters({
        id: `detailsWindow-${widgetId}`,
        ...gridOptions,
        field,
        fieldValue,
        secondField,
        secondFieldValue: secondFieldValue as string,
      })
    );
  }, [dispatch, gridOptions, widgetId, field, fieldValue, secondField, secondFieldValue]);

  const gridSettings = useMemo<DataGridSettings | undefined>(() => {
    if (columns && columns.length > 0) {
      return {
        columns,
        initialPageSize: 50,
        stopRequestsOnNoData: true,
        filterLocation: 1,
        maxHeight: 1000,
      };
    }
  }, [columns]);

  const onButtonClick = () => {
    exportWidgetHandler('EXCEL', true);
  };

  return (
    <ModalDialog
      visible={visible}
      buttons={closeModalButton.current}
      header="Details Window"
      hidden={onDialogHidden}
      maxDialogWidth="65vw"
      minDialogHeight="35vh"
      maxDialogHeight="65vh"
      minDialogWidth="45vw"
      transitionDuration={400}
    >
      {isError || columnRateError ? (
        <ErrorDataLabel />
      ) : !gridSettings ? (
        <LoaderComponent />
      ) : (
        <div className="flex flex-col">
          <div className="m-2">
            <Button onClick={onButtonClick} type="default" variant="alternative" fullWidth={false} htmlType="button">
              {String(t('exportAsExcel'))}
            </Button>
          </div>
          <DataGrid
            data={dataRetrieval}
            gridSettings={gridSettings}
            onRowSelectionChange={(_gridId, selectedRows) => {
              if (selectedRows.length > 0 && createDetailsUrl) {
                const selectedRow = selectedRows[0];
                const baseUrl = createDetailsUrl(String(selectedRow.formId));
                window.open(`${baseUrl}${selectedRow.recordId}`);
              }
            }}
            customSettingsMenuItems={[]}
          />
        </div>
      )}
    </ModalDialog>
  );
}

const isDateLike = (value: unknown): boolean => {
  if (typeof value !== 'string' || value.trim() === '') return false;
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(parsed);
};
